<script lang="ts">
	//Services
	import { format } from 'svelte-i18n';
	import { hapticFeedback } from '../../../services/native_container_requests_ios';
	//Svelte hooks
	import { beforeUpdate, onMount } from 'svelte';
	//Helpers
	import { get_local_ip, is_gen_2, isRgbCct, real_id } from '../../../helpers/interface_helpers';
	import { unlockPin } from '../../../helpers/pin_lock_helpers';
	import { capFirstChar } from '../../../helpers/utilities';
	import { changeFirmware } from '../../../helpers/rgbw2_fw_update';
	import { changeLightMode } from 'src/helpers/light_mode_change';
	//Stores
	import { pin_unlocked_devices } from 'src/stores/device-runtime/pin-unlocked';
		import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
import { devices } from 'src/stores/lists/devices';
import { os } from 'src/stores/session/native-bridge';
	//Types
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	//Design system components
	import { Button, ButtonGroup, ControlsContainer, toast } from '@Shelly/shelly-design-system';
	//Design system helpers
	import { classMap } from '@Shelly/shelly-design-system/helpers/component-utils';
	//Components
	import PinUnlock from '../../../components/PinUnlock.svelte';
	import ModalPrompt from '../../../components/ModalPrompt.svelte';
	import ControlEffects from './ControlEffects.svelte';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import {
		isDeviceOff,
		isNightModeEnabled,
		supportsColorModeChange,
	} from 'src/helpers/night_mode_helpers';

	$: settingsStore = getDeviceSettings(device.id);
	export let device,
		settings,
		mode,
		disabled = false;

	const isRGBW2 = device.type === 'SHRGBW2';
	const _isRgbCct = isRgbCct(device.type);
	const isGen2 = is_gen_2(device);

	let loading = false;
	let local_ip = false;
	let pinLocked;
	let showModal = false;
	let showModalPrompt = false;
	let hasLights = false;

	let callbackValue;

	let colorType: string = 'color';
	let props = {};

	$: statusStore = getDeviceStatus(device.id);
	$: secondaryControlsDisabled = $statusStore?.online && settings
			? isDeviceOff(device, $statusStore) &&
				isNightModeEnabled(device, $settingsStore) &&
				(supportsColorModeChange(device.type) || isRgbCct(device.type))
			: disabled;

	onMount(() => {
		local_ip = get_local_ip(device.id);
	});

	beforeUpdate(() => {
		colorType = device.category === 'light' ? mode : 'white';
		if (_isRgbCct) {
			// For rgbcct devices, the component is always 'rgbcct:0', mode is rgb or cct
			colorType = settings?.['rgbcct:0']?.mode === 'rgb' ? 'color' : 'white';
		}
		if (mode === 'rgb') colorType = 'color'; //this is for v-tac g3 bulb
		if (mode === 'cct') colorType = 'white'; //this is for v-tac g3 bulb
		pinLocked =
			typeof $pin_unlocked_devices[device.id] === 'undefined' && $devices[device.id]?.pin_lock;
	});

	const openModalPrompt = (_mode) => {
		showModalPrompt = true;
		props = {
			title: capFirstChar($format('sub_settings_title_firmware_version')),
			value: _mode,
			callback: () => changeFirmware(device, _mode, local_ip),
			button_1: $format('button_update'),
			button_2: $format('button_cancel'),
			text: $format('alert_change_mode_updates_fw', {
				default:
					"This mode will change you device's current firmware. This might take up to 5 minutes. Do you want to continue?",
			}),
		};
	};

	const handleModeChange = async (newMode) => {
		if (!pinLocked) {
			if (isRGBW2) {
				return openModalPrompt(newMode);
			}
			if ($os === 'IOS') {
				hapticFeedback('buttonPressed');
			}

			colorType = newMode;

			loading = true;
			const res: SettingRequest = await changeLightMode(device, settings, newMode);
			if (!res?.isok) {
				toast.open({ type: 'danger', title: $format('error_global') });
			}
			loading = false;
		} else {
			callbackValue = newMode;
			showModal = true;
		}
	};

	const handlePin = () => {
		pinLocked = unlockPin(device, pinLocked);
		handleModeChange(callbackValue);
	};
</script>

<!-- <ModeSwitch {mode} on:mode_change={data => handle_req(data.detail)} /> -->

{#if $showNewLayout}
	<ButtonGroup
		id="buttongroup-3g6e45e"
		aria-label={$format('button_group', { default: 'Button group' })}
		class="mb-m"
		orientation="horizontal"
		noWrap
	>
		<Button
			class={classMap({
				active: colorType === 'color',
			})}
			width="full"
			size="m"
			loading={loading && colorType === 'white'}
			on:click={() => handleModeChange('color')}
			disabled={secondaryControlsDisabled}
		>
			{$format('button_color', { default: 'Color' })}
		</Button>
		<Button
			class={classMap({
				active: colorType === 'white',
			})}
			width="full"
			size="m"
			loading={loading && colorType === 'color'}
			on:click={() => handleModeChange('white')}
			disabled={secondaryControlsDisabled}
		>
			{$format('button_white', { default: 'White' })}
		</Button>
	</ButtonGroup>
	{#if colorType === 'color' && !isGen2}
		<ControlEffects bind:hasLights {device} />
	{/if}
{:else}
	<ControlsContainer class="mb-m" color={undefined}>
		<svelte:fragment slot="first">
			<ButtonGroup
				id="buttongroup-3g6e45e"
				aria-label={$format('button_group', { default: 'Button group' })}
				class={colorType === 'color' ? 'mb-s' : ''}
			>
				<Button
					class={classMap({
						active: colorType === 'color',
					})}
					size="m"
					loading={loading && colorType === 'white'}
					on:click={() => handleModeChange('color')}
					disabled={secondaryControlsDisabled}
				>
					{$format('button_color', { default: 'Color' })}
				</Button>
				<Button
					class={classMap({
						active: colorType === 'white',
					})}
					size="m"
					loading={loading && colorType === 'color'}
					on:click={() => handleModeChange('white')}
					disabled={secondaryControlsDisabled}
				>
					{$format('button_white', { default: 'White' })}
				</Button>
			</ButtonGroup>
			{#if colorType === 'color' && !isGen2}
				<ControlEffects bind:hasLights {device} />
			{/if}
		</svelte:fragment>
	</ControlsContainer>
{/if}

{#if showModal}
	<PinUnlock bind:showModal on:unlock={handlePin} on:cancel={() => (showModal = false)} />
{/if}

{#if showModalPrompt}
	<ModalPrompt bind:showModal={showModalPrompt} {props} />
{/if}
